export interface ThaiIdValidationResult {
  valid: boolean
  error?: string
}

/**
 * Validate a Thai 13-digit national ID number.
 *
 * Algorithm:
 *   1. Must be exactly 13 digits.
 *   2. Multiply digits 1-12 by weights 13, 12, 11, … 2 respectively and sum.
 *   3. check = (11 - (sum % 11)) % 10
 *   4. The 13th digit must equal the check digit.
 *
 * Non-13-digit IDs (e.g. passport numbers) are skipped — returns valid.
 */
export function validateThaiNationalId(id: string): ThaiIdValidationResult {
  // Strip any whitespace or dashes for convenience
  const digits = id.replace(/[\s-]/g, '')

  // If not all digits, skip validation (could be passport/foreign ID)
  if (!/^\d+$/.test(digits)) {
    return { valid: true }
  }

  // Only validate 13-digit Thai IDs
  if (digits.length !== 13) {
    return { valid: true }
  }

  let sum = 0
  for (let i = 0; i < 12; i++) {
    sum += parseInt(digits[i], 10) * (13 - i)
  }

  const checkDigit = (11 - (sum % 11)) % 10
  const lastDigit = parseInt(digits[12], 10)

  if (checkDigit !== lastDigit) {
    return { valid: false, error: 'Invalid national ID (checksum mismatch)' }
  }

  return { valid: true }
}
